'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as Sentry from "@sentry/nextjs";
import { getAuthUser } from "@/hooks/useAuth";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    // Log error to console in development
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);

    // Try to get user info from localStorage to enrich the error event
    try {
      const user = getAuthUser();
      if (user) {
        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.name,
          ...user,
        });
      }
    } catch (e) {
      console.error('[ErrorBoundary] Failed to get user info:', e);
    }

    // You can also log to an error reporting service here
    // e.g., Sentry, LogRocket, etc.
    Sentry.captureException(error);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };


  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
          <div className="max-w-lg w-full">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-8 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Oops! Something went wrong
                </h1>
                <p className="text-red-100 text-sm">
                  We encountered an unexpected error
                </p>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <p className="text-gray-600 text-center">
                  Don&apos;t worry, your data is safe. You can try refreshing the page
                  or go back to the home page.
                </p>

                {/* Error details (collapsible in production) */}
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className="bg-gray-50 rounded-lg p-4 text-sm">
                    <summary className="cursor-pointer text-gray-700 font-medium flex items-center gap-2">
                      <Bug className="w-4 h-4" />
                      Error Details (Development Only)
                    </summary>
                    <div className="mt-3 space-y-2">
                      <div className="bg-red-50 border border-red-200 rounded p-3">
                        <p className="font-mono text-red-700 text-xs break-all">
                          {this.state.error.message}
                        </p>
                      </div>
                      {this.state.errorInfo?.componentStack && (
                        <div className="bg-gray-100 rounded p-3 max-h-40 overflow-auto">
                          <pre className="font-mono text-gray-600 text-xs whitespace-pre-wrap">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    onClick={this.handleReset}
                    variant="outline"
                    className="flex-1 h-11"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    onClick={this.handleReload}
                    variant="outline"
                    className="flex-1 h-11"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reload Page
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Go Home
                  </Button>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t">
                <p className="text-xs text-gray-500 text-center">
                  If this problem persists, please contact support.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
